import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardContent from '@/components/dashboard/DashboardContent';
import { Building } from "lucide-react";
import { supabase as supabaseClient } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/ui/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useBusinesses } from "@/hooks/business/useBusinesses";
import { useBusinessData } from "@/hooks/business/useBusinessData";
import { BusinessDetails } from "@/types/index";
import { QrCodeData } from "@/types/qrCode";
import PayrollManagementTab from "@/components/payroll/PayrollManagementTab";
import { useAccountSettings } from "@/context/AccountSettingsContext";
import { useDashboardTab } from "@/context/DashboardTabContext";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isEmployee, loading } = useAuth();
  const { isAccountSettingsOpen } = useAccountSettings();
  const { businessDashboardTab: activeView, setBusinessDashboardTab: setActiveView, setSelectedBusinessName, setIsAccountSettingsOpen } = useDashboardTab();

  // Initialize business data hook first
  const {
    businesses,
    setBusinesses,
    updateBusinessInList,
    loadingBusinesses,
    selectedBusinessId,
    setSelectedBusinessId,
  } = useBusinesses();

  // Redirect employees to employee portal
  useEffect(() => {
    if (!loading && user && isEmployee) {
      console.log('Dashboard: Employee detected, redirecting to employee portal');
      navigate('/employee-portal', { replace: true });
    }
  }, [user, isEmployee, loading, navigate]);

  // Redirect to create-business if user has no businesses
  useEffect(() => {
    // Don't redirect during password recovery flow
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    if (hashParams.get('type') === 'recovery') return;

    if (!loadingBusinesses && !isEmployee && businesses.length === 0) {
      console.log('Dashboard: No businesses found, redirecting to create business');
      navigate('/create-business', { replace: true });
    }
  }, [loadingBusinesses, isEmployee, businesses.length, navigate]);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Function to refresh reviews data
  const refreshReviews = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const {
    reviews,
    shopStats,
    rewards,
    customerData,
    analyticsData,
    selectedBusinessDetails,
    loadingData,
    setLoadingData,
    fetchQrCodesForBusiness, // Destructure the new function
  } = useBusinessData(selectedBusinessId, businesses, refreshTrigger);

  const [qrCodes, setQrCodes] = useState<QrCodeData[]>([]); // New state for QR codes
  const [qrCodesLoading, setQrCodesLoading] = useState(false); // New state for QR codes loading

  const [businessDeleteDialogOpen, setBusinessDeleteDialogOpen] = useState(false);
  const [qrCodeDeleteDialogOpen, setQrCodeDeleteDialogOpen] = useState(false);
  const [qrCodeToDelete, setQrCodeToDelete] = useState<string | null>(null);

  const [replyingToReviewId, setReplyingToReviewId] = useState<string | null>(null);

  const selectedBusinessName = businesses.find(b => b.id === selectedBusinessId)?.name || "Business";

  // Update toast context when business or account settings changes
  useEffect(() => {
    setSelectedBusinessName(selectedBusinessName !== "Business" ? selectedBusinessName : null);
  }, [selectedBusinessName, setSelectedBusinessName]);

  useEffect(() => {
    setIsAccountSettingsOpen(isAccountSettingsOpen);
  }, [isAccountSettingsOpen, setIsAccountSettingsOpen]);

  const handleUpdateSuccess = (updatedDetails: Partial<BusinessDetails>) => {
    if (selectedBusinessId) {
      updateBusinessInList(selectedBusinessId, updatedDetails);
      toast.info("Business details updated in the list.", { businessName: selectedBusinessName });
    }
  };

  const handleDeleteBusiness = async () => {
    if (!selectedBusinessId) return;

    setLoadingData(true);
    try {
      const { error } = await supabaseClient
        .from("businesses")
        .delete()
        .eq("id", selectedBusinessId);

      if (error) throw error;

      toast.success("Business deleted successfully!", { businessName: selectedBusinessName });

      const remainingBusinesses = businesses.filter(b => b.id !== selectedBusinessId);
      setBusinesses(remainingBusinesses);

      const nextBusinessId = remainingBusinesses.length > 0 ? remainingBusinesses[0].id : null;
      setSelectedBusinessId(nextBusinessId);

      setBusinessDeleteDialogOpen(false);

    } catch (error: any) {
      toast.error("Failed to delete business", {
        description: error.message || "An unexpected error occurred.",
        businessName: selectedBusinessName
      });
    } finally {
      setLoadingData(false);
    }
  };

  const openDeleteQrCodeDialog = (qrCodeId: string) => {
    setQrCodeToDelete(qrCodeId);
    setQrCodeDeleteDialogOpen(true);
  };

  const handleConfirmDeleteQrCode = async () => {
    if (!qrCodeToDelete || !selectedBusinessId) {
      return;
    }

    try {
      const { error } = await supabaseClient
        .from("qr_codes")
        .delete()
        .eq("id", qrCodeToDelete)
        .eq("business_id", selectedBusinessId);

      if (error) throw error;

      toast.success("QR code deleted successfully!", { businessName: selectedBusinessName });
      setQrCodes(prevQrCodes => prevQrCodes.filter(qrCode => qrCode.id !== qrCodeToDelete)); // Optimistic update
      // No need for setRefreshTrigger here for QR codes, as we'll explicitly refetch
      // setRefreshTrigger(prev => prev + 1); // Removed this line
      // Trigger a re-fetch of QR codes specifically
      if (selectedBusinessId) {
        setQrCodesLoading(true);
        fetchQrCodesForBusiness(selectedBusinessId)
          .then(data => setQrCodes(data))
          .catch(error => console.error("Error re-fetching QR codes after delete:", error))
          .finally(() => setQrCodesLoading(false));
      }
    } catch (error: any) {
      toast.error("Failed to delete QR code", {
        description: error.message || "An unexpected error occurred.",
        businessName: selectedBusinessName
      });
    } finally {
      setQrCodeDeleteDialogOpen(false);
      setQrCodeToDelete(null);
    }
  };





  // Effect to fetch QR codes when selectedBusinessId changes or a refresh is needed
  useEffect(() => {
    if (!selectedBusinessId) {
      setQrCodes([]);
      return;
    }
    setQrCodesLoading(true);
    fetchQrCodesForBusiness(selectedBusinessId)
      .then(data => setQrCodes(data))
      .catch(error => console.error("Error fetching QR codes:", error))
      .finally(() => setQrCodesLoading(false));
  }, [selectedBusinessId, fetchQrCodesForBusiness]); // Depend on selectedBusinessId and the fetch function



  const handleBusinessCreated = (newBusiness: BusinessDetails) => {
    setBusinesses(prev => [...prev, newBusiness]);
    setSelectedBusinessId(newBusiness.id); // Select the newly created business
    setActiveView("reviews"); // Switch back to reviews tab
    toast.success(`Business "${newBusiness.name}" created successfully!`, { businessName: newBusiness.name });
  };

  // Note: loadingBusinesses skeleton is handled by SubscriptionRequiredRoute
  // Dashboard only needs to handle the "no business selected" case and tab content

  return (
    <>
      {!selectedBusinessId && businesses.length > 0 ? (
        <div className="mt-12 text-center bg-white dark:bg-card p-8 rounded-lg shadow">
          <Building className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500" />
          <h2 className="mt-6 text-2xl font-semibold dark:text-gray-100">Select a Business</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Please select a business from the dropdown above to view its dashboard.
          </p>
        </div>
      ) : activeView === "payrolls" ? (
        <PayrollManagementTab businessId={selectedBusinessId} />
      ) : (
        <DashboardContent
          business={selectedBusinessDetails}
          reviews={reviews}
          activeView={activeView}
          onDeleteQrCode={openDeleteQrCodeDialog}
          loadingData={loadingData}
          onBusinessCreated={handleBusinessCreated}
          onDeleteBusinessSuccess={handleDeleteBusiness}
          qrCodes={qrCodes}
          setQrCodes={setQrCodes}
          qrCodesLoading={qrCodesLoading}
          onRefreshReviews={refreshReviews}
        />
      )}

      <AlertDialog open={qrCodeDeleteDialogOpen} onOpenChange={setQrCodeDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Deleting this Tag will prevent new scans but won't affect past data collected through it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setQrCodeToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDeleteQrCode} className="bg-red-600 hover:bg-red-700">
              Yes, delete Tag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

};
export default Dashboard;
